// X-Api-Version：仅 .env.production 配置 VITE_API_VERSION（与 nginx 路由一致）
import { Message } from './feedback'
import axios, { type InternalAxiosRequestConfig, type AxiosInstance, type AxiosResponse } from 'axios'
import router from '@/router'
import { debounce, downloadBlob, getUserStore } from '@/utils/tools'
import feedback from '@/utils/feedback'

const getAuthorization = () => {
  const userStore = getUserStore()
  return userStore.sessionToken.tokenType + userStore.sessionToken.token
}
// 在 axios 实例创建之前定义一个变量用于缓存 refreshToken 请求的 Promise
let isRefreshing = false
let refreshTokenPromise: Promise<void> | null = null
const checkJwtExpThenRefreshToken = async () => {
  const userStore = getUserStore()
  const refreshToken = userStore.sessionToken.refreshToken
  if (!refreshToken) return

  const exp = userStore.jwt.exp
  // 如果token未过期，直接返回
  if (exp * 1000 > Date.now()) {
    return
  }

  // 如果已经有refreshToken请求正在进行，直接复用该Promise
  if (isRefreshing) {
    await refreshTokenPromise
    return
  }
  // 开始刷新token
  isRefreshing = true

  refreshTokenPromise = new Promise<void>(async (resolve, reject) => {
    try {
      const response = await axios.postForm(import.meta.env.VITE_BACK_API + '/refreshToken', { refreshToken })
      await unauthorizedJudge(response)

      if (response.data.data?.token) {
        userStore.patchSessionTokenState(response.data.data)
        userStore.exchangeLoginInfo()
        isRefreshing = false
        refreshTokenPromise = null
        resolve()
      } else {
        throw new Error('刷新token失败')
      }
    } catch (e) {
      console.error('refreshToken-error', e)
      userStore.reset()
      isRefreshing = false
      refreshTokenPromise = null
      reject(e)
    }
  })

  await refreshTokenPromise
}

const axiosInstance: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_BACK_API,
  timeout: 15000
})
// 添加请求拦截器
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    await checkJwtExpThenRefreshToken()
    // 在发送请求之前做些什么
    // console.log('config---', config)
    if ((config as any)['unsetAuthorization']) {
      delete config.headers.Authorization
    } else {
      config.headers.Authorization = getAuthorization()
    }
    // 添加API版本号到请求头
    config.headers['X-Api-Version'] = import.meta.env.VITE_API_VERSION as string
    if (config.params) {
      // 过滤掉空参数
      config.params = Object.entries(config.params)
        .filter(([key, value]) => value !== '' && value != null)
        .reduce((obj, [key, val]) => {
          const value = Array.isArray(val) ? val.map((item) => encodeURIComponent(item)).join(',') : val
          return Object.assign(obj, { [key]: value })
        }, {})
    }
    return config
  },
  (error: any) => {
    // 处理请求错误
    return Promise.reject(error)
  }
)

// 添加响应拦截器
axiosInstance.interceptors.response.use(
  async (response: AxiosResponse) => {
    // console.log('interceptors.response', response.status)
    await unauthorizedJudge(response)
    // 对响应数据做点什么
    return response
  },
  (error: any) => {
    // console.error('error----', error)
    feedback.Loading.hasLoading() ? feedback.Loading.close() : ''
    // Feedback.Message.error('请求有误，请稍后再试')
    // 处理响应错误
    return Promise.reject(error)
  }
)

//定义取消弹窗事件变量，后续根据该条件判断，避免短时间内出现多次弹窗
let cancelDialogEvent = false
const cancelDebounce = debounce(() => {
  cancelDialogEvent = false
}, 1000)
const unauthorizedJudge = async (response: AxiosResponse) => {
  //已经点击取消了，就没必要再次弹出一次
  console.log('cancelDialogEvent', cancelDialogEvent)

  if (cancelDialogEvent) {
    //防抖，避免多次请求同时出现未授权，只有最后一次请求完，才允许后续弹窗的可能性出现
    cancelDebounce()
    return
  }
  if (response.data.code == 401) {
    if (window.location.pathname !== '/login') {
      const confirm = await feedback.Modal.error(response.data.message, '', {
        confirmButtonText: '跳转登录',
        cancelButtonText: '取消'
      })
      if (confirm) {
        const userStore = getUserStore()
        userStore.reset()
        cancelDialogEvent = false
        //接口请求401，则未授权或token过期，跳转到登录页
        router.push('/login?expire=1')
      } else {
        cancelDialogEvent = true
      }
    }
  }
}

export interface ApiResult<T> {
  code: number | string
  message: string
  data: T
}

export class request {
  static BackApi = import.meta.env.VITE_BACK_API

  // Feedback反馈组件
  static feedback = feedback

  static getAuthorizationHeaders = async (): Promise<any> => {
    await checkJwtExpThenRefreshToken()
    return { Authorization: getAuthorization() }
  }

  static async get<T = any>(url: string, params?: any, config?: any): Promise<ApiResult<T>> {
    const response = await axiosInstance.get<ApiResult<T>>(url, { params, ...config })
    return response.data
  }
  /**
   * post json
   */
  static async post<T = any>(url: string, data?: any, config?: any): Promise<ApiResult<T>> {
    const response = await axiosInstance.post<ApiResult<T>>(url, data, { ...config })
    return response.data
  }
  /**
   * post urlencoded
   */
  static async postParams<T = any>(url: string, params?: any, config?: any): Promise<ApiResult<T>> {
    const response = await axiosInstance.post<ApiResult<T>>(url, params, {
      ...config,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    })
    return response.data
  }

  /**
   * 上传
   * @param url 上传服务器地址
   * @param data  {filePath: 本地临时文件路径, uploadKeyName: 后端接口上传文件的key名称参数 MultipartFile file}
   */
  static async upload<T>(url: string, data: { filePath: string | File; [key: string]: any }, config?: any): Promise<ApiResult<T>> {
    const formData = new FormData()
    const uploadKeyName = data.uploadKeyName || 'file'

    // 添加文件到 FormData
    if (data.filePath) {
      formData.append(uploadKeyName, data.filePath)
    }

    // 添加其他参数到 FormData
    Object.keys(data).forEach((key) => {
      if (key !== 'filePath' && key !== 'uploadKeyName') {
        formData.append(key, data[key])
      }
    })

    // 使用 post 方法上传
    const response = await axiosInstance.post<ApiResult<T>>(url, formData, {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  }

  static async put<T = any>(url: string, data?: any, config?: any): Promise<ApiResult<T>> {
    const response = await axiosInstance.put<ApiResult<T>>(url, data, { ...config })
    return response.data
  }
  static async delete<T = any>(url: string, params?: any, config?: any): Promise<ApiResult<T>> {
    const response = await axiosInstance.delete<ApiResult<T>>(url, { params, ...config })
    return response.data
  }

  static resultFeedback<T = any>(res: ApiResult<T>, success?: string, error?: string): boolean {
    console.log('resultFeedback', res)
    if (res.code == 200) {
      feedback.Message.success(success ? success : res.message ? res.message : '操作成功')
      return true
    } else {
      feedback.Message.error(error ? error : res.message ? res.message : '操作失败')
      return false
    }
  }

  static isCode<T = any>(res: ApiResult<T>, code: number): typeof request | undefined {
    return res.code == code ? request : undefined
  }

  static resultSuccess<T = any>(res: ApiResult<T>, callback: Function): typeof request {
    res.code == 200 && callback()
    return request
  }

  static resultError<T = any>(res: ApiResult<T>, callback: Function): typeof request {
    res.code != 200 && callback()
    return request
  }

  static success<T = any>(res: ApiResult<T>): boolean {
    return res.code == 200
  }

  static loading(title?: string) {
    feedback.Loading.loading(title ? { text: title } : {})
    return request
  }

  static loadingClose() {
    feedback.Loading.close()
    return request
  }

  static doSomeThing(task: Function) {
    task()
    return request
  }

  static async normalExport(url: string, params?: any): Promise<void> {
    request.doSomeThing(() => {
      feedback.Message.success('处理中...')
    })
    const response = await axiosInstance.get(url, { params, responseType: 'blob' })
    downloadBlob(response)
  }

  static async fileDownload(url: string, params?: any): Promise<void> {
    request.doSomeThing(() => {
      feedback.Message.success('下载中...')
    })
    const response = await axiosInstance.get(url, { params, responseType: 'blob' })
    downloadBlob(response)
  }

  static async normalDelete(url: string, params?: any, callback?: Function): Promise<void> {
    console.log('normalDelete', params)
    request
      .delete(url, params)
      .then((response) => {
        if (response.code == 200) {
          feedback.Message.success('删除成功')
          callback && callback()
        } else {
          return Promise.reject(response.message)
        }
      })
      .catch((e) => {
        console.error(e)
        feedback.Message.error(e || '删除失败')
      })
  }
}
